import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RotateCcw, Timer as TimerIcon, Trophy } from 'lucide-react';

// SPC-flavoured icons for the memory pairs
const ICONS = ['🏃', '👟', '🏆', '⚡️', '🔥', '💪', '🎯', '🥇'];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function fmt(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
}

export default function MemoryMatch() {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]); // indices currently face-up (max 2)
  const [matched, setMatched] = useState([]); // indices of solved cards
  const [moves, setMoves] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(0);
  const timer = useRef(null);
  const [best, setBest] = useState(() => {
    try { return JSON.parse(localStorage.getItem('spc_memory_best')) || null; }
    catch { return null; }
  });

  const start = useCallback(() => {
    const deck = shuffle([...ICONS, ...ICONS]).map((v, i) => ({ id: i, value: v }));
    setCards(deck);
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setStartedAt(null);
    setNow(0);
  }, []);

  useEffect(() => { start(); }, [start]);

  // Timer
  useEffect(() => {
    if (!startedAt) return;
    timer.current = setInterval(() => setNow(Date.now() - startedAt), 100);
    return () => clearInterval(timer.current);
  }, [startedAt]);

  // Save best when finished
  const finished = cards.length > 0 && matched.length === cards.length;
  useEffect(() => {
    if (!finished || !startedAt) return;
    clearInterval(timer.current);
    const total = Date.now() - startedAt;
    setNow(total);
    if (!best || total < best.timeMs || (total === best.timeMs && moves < best.moves)) {
      const next = { timeMs: total, moves };
      setBest(next);
      try { localStorage.setItem('spc_memory_best', JSON.stringify(next)); } catch {}
    }
  }, [finished]); // eslint-disable-line

  const flip = (i) => {
    if (flipped.includes(i) || matched.includes(i)) return;
    if (flipped.length === 2) return;
    if (!startedAt) setStartedAt(Date.now());
    const next = [...flipped, i];
    setFlipped(next);
    if (next.length === 2) {
      setMoves(m => m + 1);
      const [a, b] = next;
      if (cards[a].value === cards[b].value) {
        setTimeout(() => {
          setMatched(m => [...m, a, b]);
          setFlipped([]);
        }, 500);
      } else {
        setTimeout(() => setFlipped([]), 900);
      }
    }
  };

  return (
    <div className="solo-game" data-testid="solo-memory">
      <div className="solo-head">
        <div className="solo-title"><span style={{ fontSize: 20 }}>🧠</span><span>Memory Match</span></div>
        <div className="solo-score">
          <span className="pill"><TimerIcon size={12} /> {fmt(now)}</span>
          <span className="pill">Moves {moves}</span>
          {best && <span className="pill yellow"><Trophy size={12} /> Best {fmt(best.timeMs)} / {best.moves}</span>}
        </div>
      </div>

      {finished && (
        <div className="solo-status win" data-testid="solo-memory-status">Solved in {fmt(now)} with {moves} moves! 🎉</div>
      )}

      <div className="memory-board" data-testid="solo-memory-board">
        {cards.map((c, i) => {
          const shown = flipped.includes(i) || matched.includes(i);
          return (
            <button
              key={c.id}
              data-testid={`memory-card-${i}`}
              className={`mem-card ${shown ? 'shown' : ''} ${matched.includes(i) ? 'matched' : ''}`}
              onClick={() => flip(i)}
              aria-label={shown ? c.value : 'Hidden card'}
            >
              <span className="mem-face front">?</span>
              <span className="mem-face back">{c.value}</span>
            </button>
          );
        })}
      </div>

      <div className="solo-actions">
        <button className="btn" data-testid="memory-restart" onClick={start}><RotateCcw size={15} /> New shuffle</button>
      </div>
    </div>
  );
}
